/**
 * FeedbackAdminPage — admin triage surface for user-submitted bug
 * reports. Active / Resolved tabs; expandable cards; Fix flow
 * (notes + flags for next agent session) and Resolve flow.
 *
 * Route: /admin/feedback (admin only).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronUp, Loader2, Wrench, CheckCircle2, X, Filter, Flag } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { usePageHeader } from '../context/PageHeaderContext';

const TAB_ACTIVE = 'active';
const TAB_RESOLVED = 'resolved';

const Avatar = ({ name }) => {
  const initials = (name || '').split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';
  return (
    <div className="w-10 h-10 rounded-full bg-[#586D52]/15 text-[#586D52] flex items-center justify-center font-semibold text-sm flex-shrink-0">
      {initials}
    </div>
  );
};

const ReportCard = ({ report, onRefresh, defaultOpen = false }) => {
  const { api } = useAuth();
  const [open, setOpen] = useState(defaultOpen);
  const [showFix, setShowFix] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const preview = (report.description || '').split('\n')[0].slice(0, 90);
  const tooLong = (report.description || '').length > 90;

  const submitFix = async () => {
    if (note.trim().length < 1) { toast.error('Add a note before flagging.'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/feedback/${report.id}/fix`, { note: note.trim() });
      toast.success('Flagged for next session — agent will surface this on resume.');
      setNote(''); setShowFix(false);
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to flag');
    } finally { setBusy(false); }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/feedback/${report.id}/resolve`);
      toast.success('Moved to Resolved');
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to resolve');
    } finally { setBusy(false); }
  };

  return (
    <Card className="border border-gray-200 dark:border-[#2a2a2a] shadow-sm overflow-hidden" data-testid={`feedback-card-${report.id}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-[#222] text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="font-semibold text-sm truncate">{report.page} <span className="text-muted-foreground font-normal">›</span> {report.section}</p>
            {report.flagged_for_agent && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full" title="Flagged for next agent session">flagged</span>
            )}
          </div>
          <p className="text-xs text-muted-foreground truncate">{preview}{tooLong ? '…' : ''}</p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-[#2a2a2a] space-y-3">
          <div className="flex items-center gap-3 pt-2">
            <Avatar name={report.user_full_name} />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{report.user_full_name}</p>
              <p className="text-xs text-muted-foreground capitalize">{report.user_role}{report.user_state ? ` · ${report.user_state}` : ''}</p>
            </div>
            <p className="text-[10px] text-muted-foreground">{report.submitted_at ? new Date(report.submitted_at).toLocaleString() : ''}</p>
          </div>

          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 font-semibold">Description</p>
            <p className="text-sm whitespace-pre-wrap leading-relaxed" data-testid={`feedback-card-${report.id}-description`}>{report.description}</p>
          </div>

          {(report.admin_notes || []).length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1 font-semibold">Admin notes</p>
              <div className="space-y-2">
                {report.admin_notes.map((n, i) => (
                  <div key={i} className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/40 rounded-lg p-2 text-xs">
                    <p className="font-semibold text-amber-700 dark:text-amber-300">{n.admin_name} <span className="font-normal text-muted-foreground">· {n.created_at ? new Date(n.created_at).toLocaleString() : ''}</span></p>
                    <p className="mt-1 whitespace-pre-wrap">{n.note}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.status === 'resolved' && report.resolved_by && (
            <div className="text-xs text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-2">
              Resolved by <strong>{report.resolved_by.name}</strong> on {report.resolved_at ? new Date(report.resolved_at).toLocaleString() : ''}.
            </div>
          )}

          {report.status === 'active' && (
            showFix ? (
              <div className="space-y-2 bg-amber-50/40 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-900/40 rounded-lg p-3">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-200">Add notes for the next agent session</p>
                <Textarea
                  rows={4}
                  placeholder="What's the priority? Any context the agent should know? Reproduction details?"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid={`feedback-fix-note-${report.id}`}
                />
                <div className="flex items-center gap-2">
                  <Button onClick={submitFix} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white" size="sm" data-testid={`feedback-fix-submit-${report.id}`}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Wrench className="w-4 h-4 mr-1.5" />Flag for next session</>}
                  </Button>
                  <Button onClick={() => { setShowFix(false); setNote(''); }} variant="ghost" size="sm" disabled={busy}><X className="w-4 h-4" /></Button>
                </div>
                <p className="text-[10px] text-muted-foreground">Flagging only notifies the agent — the agent will NOT auto-fix; you'll be prompted on next sign-in.</p>
              </div>
            ) : (
              <div className="flex items-center gap-2 pt-1">
                <Button onClick={() => setShowFix(true)} className="bg-amber-500 hover:bg-amber-600 text-white" size="sm" data-testid={`feedback-fix-btn-${report.id}`}>
                  <Wrench className="w-4 h-4 mr-1.5" />Fix
                </Button>
                <Button onClick={resolve} disabled={busy} variant="outline" size="sm" data-testid={`feedback-resolve-btn-${report.id}`}>
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />Resolve
                </Button>
              </div>
            )
          )}
        </div>
      )}
    </Card>
  );
};

const FeedbackAdminPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { api, user } = useAuth();
  const [tab, setTab] = useState(TAB_ACTIVE);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters — applied client-side over the loaded list. Persist
  // with the user's session so navigating away and back doesn't
  // wipe their working set.
  const [filterPage, setFilterPage] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  // Deep-link target — when /admin/feedback?reportId=… is hit
  // (from the bell notification), auto-expand the matching card,
  // switch to whichever tab contains it, and scroll it into view.
  const deepLinkId = searchParams.get('reportId');

  usePageHeader({
    title: 'Feedback Reports',
    back: '/feedback',
  }, []);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/feedback', { params: { status: tab } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reports');
    } finally { setLoading(false); }
  }, [api, tab]);

  useEffect(() => { refresh(); }, [refresh]);

  // Deep-link: when ?reportId= is set, fetch that single report
  // first to determine its status, then switch to the right tab so
  // it appears in the loaded list. After list refresh, scroll it
  // into view.
  useEffect(() => {
    if (!deepLinkId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/feedback/${deepLinkId}`);
        if (cancelled) return;
        if (data?.status && data.status !== tab) {
          setTab(data.status);
        }
      } catch {/* invalid id — ignore */}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId]);

  useEffect(() => {
    if (!deepLinkId || loading) return;
    // Wait one tick for the cards to mount, then scroll the deep-
    // linked one into view. Card mounts auto-expanded via
    // defaultOpen prop on the ReportCard.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="feedback-card-${deepLinkId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => clearTimeout(t);
  }, [deepLinkId, loading, items]);

  // Distinct page + role lists derived from the loaded items so
  // the filter dropdowns only offer values that actually exist.
  const distinctPages = useMemo(() => {
    const set = new Set(items.map((i) => i.page).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);
  const distinctRoles = useMemo(() => {
    const set = new Set(items.map((i) => i.user_role).filter(Boolean));
    return Array.from(set).sort();
  }, [items]);

  const visibleItems = useMemo(() => {
    return items.filter((i) => {
      if (filterPage !== 'all' && i.page !== filterPage) return false;
      if (filterRole !== 'all' && i.user_role !== filterRole) return false;
      if (flaggedOnly && !i.flagged_for_agent) return false;
      return true;
    });
  }, [items, filterPage, filterRole, flaggedOnly]);

  const filtersActive = filterPage !== 'all' || filterRole !== 'all' || flaggedOnly;
  const clearFilters = () => { setFilterPage('all'); setFilterRole('all'); setFlaggedOnly(false); };

  if (user?.role !== 'admin') {
    return <div className="p-8 text-center"><p>Admin only.</p></div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 pb-24" data-testid="feedback-admin-page">
      <div className="flex items-center gap-2 mb-4 border-b" data-testid="feedback-admin-tabs">
        {[
          { id: TAB_ACTIVE, label: 'Active' },
          { id: TAB_RESOLVED, label: 'Resolved' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-[#586D52] text-[#586D52]' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            data-testid={`feedback-admin-tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters — page, reporter role, flagged-only. Hidden when
          there are no reports loaded (nothing to filter). */}
      {!loading && items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="feedback-admin-filters">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mr-1">
            <Filter className="w-3.5 h-3.5" /> Filters:
          </div>
          <Select value={filterPage} onValueChange={setFilterPage}>
            <SelectTrigger className="h-8 w-auto min-w-[140px] text-xs px-3" data-testid="feedback-filter-page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="feedback-filter-page-all">All pages</SelectItem>
              {distinctPages.map((p) => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterRole} onValueChange={setFilterRole}>
            <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs px-3" data-testid="feedback-filter-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="feedback-filter-role-all">All roles</SelectItem>
              {distinctRoles.map((r) => (
                <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={() => setFlaggedOnly((v) => !v)}
            className={`inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-medium border transition-colors ${flaggedOnly ? 'bg-amber-100 border-amber-300 text-amber-800 dark:bg-amber-900/40 dark:border-amber-700 dark:text-amber-200' : 'bg-white dark:bg-[#1a1a1a] border-input text-muted-foreground hover:text-foreground'}`}
            data-testid="feedback-filter-flagged-only"
            aria-pressed={flaggedOnly}
          >
            <Flag className="w-3.5 h-3.5" /> Flagged only
          </button>
          {filtersActive && (
            <button
              onClick={clearFilters}
              className="inline-flex items-center gap-1 h-8 px-2 text-xs text-muted-foreground hover:text-foreground"
              data-testid="feedback-filter-clear"
            >
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          <span className="ml-auto text-xs text-muted-foreground">
            {visibleItems.length} of {items.length}
          </span>
        </div>
      )}

      {loading && (<div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>)}

      {!loading && items.length === 0 && (
        <p className="text-center text-muted-foreground py-10">{tab === TAB_ACTIVE ? 'No active reports.' : 'No resolved reports.'}</p>
      )}

      {!loading && items.length > 0 && visibleItems.length === 0 && (
        <p className="text-center text-muted-foreground py-10" data-testid="feedback-admin-empty-filtered">No reports match the current filters.</p>
      )}

      {!loading && visibleItems.length > 0 && (
        <div className="space-y-2" data-testid="feedback-admin-list">
          {visibleItems.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              onRefresh={refresh}
              defaultOpen={r.id === deepLinkId}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FeedbackAdminPage;
